import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Button, 
  Box, 
  List, 
  ListItem, 
  ListItemText, 
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { KeyboardArrowUp as ArrowUpIcon } from '@mui/icons-material';
import Gallery from './Gallery';
import LoadErrorScreen from './LoadErrorScreen';
import CardTitleRow from './CardTitleRow';
import './ItemPage.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const ItemPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [item, setItem] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [branchAddress, setBranchAddress] = useState(null);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [rentDialogOpen, setRentDialogOpen] = useState(false);
  const [rentalDays, setRentalDays] = useState(1);
  const [rentalStartDate, setRentalStartDate] = useState('');
  const [rentalEndDate, setRentalEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchItem = async () => {
      setPageLoading(true);
      setLoadError(false);
      setNotFound(false);
      try {
        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/items/${id}`);
        if (response.status === 404) {
          setNotFound(true);
          setItem(null);
          return;
        }
        if (!response.ok) throw new Error('Ошибка загрузки');
        const data = await response.json();
        console.log('📦 Данные товара:', data);
        console.log('💰 depositPerUnit из БД:', data.depositPerUnit);
        setItem(data);

        // Получаем адрес филиала, если есть branchId
        if (data.branchId) {
          try {
            const branchResponse = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/admin/branches/by-short/${data.branchId}`);
            if (branchResponse.ok) {
              const branchData = await branchResponse.json();
              setBranchAddress(branchData.address);
            }
          } catch (error) {
            console.error('Ошибка загрузки адреса филиала:', error);
          }
        }
      } catch (error) {
        console.error('Ошибка загрузки данных товара:', error);
        setLoadError(true);
        setItem(null);
      } finally {
        setPageLoading(false);
      }
    };

    const handleScroll = () => {
      setShowScrollToTop(window.scrollY > 200);
    };

    fetchItem();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [id]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAddToCart = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      // Перенаправляем на страницу входа или показываем модальное окно входа
      if (window.confirm('Для добавления товара в корзину необходимо войти в систему. Перейти на страницу входа?')) {
        navigate('/login', { state: { redirectTo: `${location.pathname}${location.search}` } });
      }
      return;
    }

    if (!item || !item._id) {
      setError('Товар не найден');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/cart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          itemId: item._id,
          quantity: 1
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Ошибка добавления в корзину' }));
        throw new Error(errorData.message || 'Ошибка добавления в корзину');
      }

      setSuccess(true);
      
      // Перенаправляем в корзину через 1 секунду
      setTimeout(() => {
        navigate('/cart');
      }, 1000);
    } catch (err) {
      console.error('Ошибка добавления в корзину:', err);
      setError(err.message || 'Произошла ошибка при добавлении товара в корзину');
    } finally {
      setLoading(false);
    }
  };

  // Старый метод handleRentClick - оставляем для обратной совместимости, но не используем
  const handleRentClick = () => {
    handleAddToCart();
  };

  const handleRentalDaysChange = (days) => {
    const daysNum = parseInt(days) || 1;
    setRentalDays(daysNum);
    
    if (daysNum === 1) {
      // Для однодневной аренды дата начала и окончания одна и та же
      if (rentalStartDate) {
        setRentalEndDate(rentalStartDate);
      }
    } else {
      // Для многодневной аренды считаем дату окончания
      if (rentalStartDate) {
        const start = new Date(rentalStartDate);
        const end = new Date(start);
        end.setDate(end.getDate() + daysNum - 1); // -1 потому что включаем день начала
        setRentalEndDate(end.toISOString().split('T')[0]);
      }
    }
  };

  const handleStartDateChange = (date) => {
    setRentalStartDate(date);
    if (rentalDays === 1) {
      // Для однодневной аренды дата начала и окончания одна и та же
      setRentalEndDate(date);
    } else if (date && rentalDays) {
      const start = new Date(date);
      const end = new Date(start);
      end.setDate(end.getDate() + rentalDays - 1); // -1 потому что включаем день начала
      setRentalEndDate(end.toISOString().split('T')[0]);
    }
  };

  // Функция для получения суммы залога (та же логика, что в getParameters)
  const getDepositValue = () => {
    if (!item) return 0;
    
    let depositValue = null;
    if (item.specificFields && item.specificFields.depositPerUnit !== undefined && item.specificFields.depositPerUnit !== null) {
      depositValue = Number(item.specificFields.depositPerUnit);
    } else if (item.depositPerUnit !== undefined && item.depositPerUnit !== null) {
      depositValue = Number(item.depositPerUnit);
    }
    
    return depositValue !== null && !isNaN(depositValue) && depositValue > 0 ? depositValue : 0;
  };

  const handleCreateOrder = async () => {
    if (!item || !item.branchId) {
      setError('Не указан филиал для товара');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Необходимо войти в систему');
        return;
      }

      // Определяем период аренды
      let from, to, unit, periodAmount;
      
      if (rentalDays === 1) {
        // Однодневная аренда: одна дата, время с 10:00 до 21:00, но цена за полный день
        const date = new Date(rentalStartDate);
        from = new Date(date);
        from.setHours(10, 0, 0, 0); // 10:00
        to = new Date(date);
        to.setHours(21, 0, 0, 0); // 21:00
        // Для однодневной аренды unit остается 'days', periodAmount = 1, цена за полный день
        unit = 'days';
        periodAmount = 1; // 1 день, но время с 10:00 до 21:00
      } else if (rentalStartDate && rentalEndDate) {
        // Многодневная аренда: от начала дня начала до конца дня окончания
        from = new Date(rentalStartDate);
        from.setHours(0, 0, 0, 0);
        to = new Date(rentalEndDate);
        to.setHours(23, 59, 59, 999);
        unit = 'days';
        // Считаем количество дней включая оба дня
        periodAmount = Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1;
      } else {
        setError('Укажите период аренды');
        return;
      }

      // Получаем ObjectId филиала, если branchId - строка типа "b2"
      let branchIdToSend = item.branchId;
      
      // Если branchId - строка (не ObjectId), получаем полный объект филиала для получения _id
      if (typeof item.branchId === 'string' && !item.branchId.match(/^[0-9a-fA-F]{24}$/)) {
        try {
          const branchResponse = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/admin/branches/by-short/${item.branchId}`);
          if (branchResponse.ok) {
            const branchData = await branchResponse.json();
            branchIdToSend = branchData._id;
          }
        } catch (err) {
          console.error('Ошибка получения филиала:', err);
          // Продолжаем с исходным branchId - сервер сам попробует разрешить
        }
      }

      const orderData = {
        items: [{
          itemId: item._id,
          quantity: 1
        }],
        branchId: branchIdToSend,
        from: from.toISOString(),
        to: to.toISOString(),
        unit: unit,
        rentalDays: rentalDays
      };

      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Ошибка создания заказа');
      }

      const order = await response.json();
      setSuccess(true);
      
      // Закрываем диалог через 2 секунды и перенаправляем на страницу заказов
      setTimeout(() => {
        setRentDialogOpen(false);
        navigate('/orders');
      }, 2000);
    } catch (err) {
      console.error('Ошибка создания заказа:', err);
      setError(err.message || 'Произошла ошибка при создании заказа');
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="loading-container">
        <div className="double-ring"></div>
      </div>
    );
  }

  if (loadError) {
    return <LoadErrorScreen />;
  }

  if (!item) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Товар не найден
        </Typography>
        <Button onClick={() => navigate('/rental')} sx={{ mt: 2, color: '#8B7D6B' }}>
          К каталогу аренды
        </Button>
      </Container>
    );
  }

  // Нормализуем и убираем дубликаты фотографий перед передачей в Gallery
  const normalizePhotos = (photos) => {
    if (!photos || !Array.isArray(photos) || photos.length === 0) {
      return [];
    }

    const seenUrls = new Set();
    const normalized = [];

    // Сначала нормализуем все URL
    const normalizedUrls = photos
      .filter(photo => photo && typeof photo === 'string' && photo.trim() !== '')
      .map(photo => {
        const trimmed = photo.trim();
        if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
          return trimmed;
        }
        if (trimmed.startsWith('/')) {
          return `${process.env.REACT_APP_SERVER_URL}${trimmed}`;
        }
        return `${process.env.REACT_APP_SERVER_URL}/${trimmed}`;
      })
      .filter(url => url !== null);

    // Затем убираем дубликаты, сохраняя порядок
    for (const url of normalizedUrls) {
      if (!seenUrls.has(url)) {
        seenUrls.add(url);
        normalized.push(url);
      }
    }

    return normalized;
  };

  const images = normalizePhotos(item.photos || []);
  console.log('🖼️ Нормализованные изображения для галереи:', images);

  // Маппинг полей на читаемые названия
  const fieldLabels = {
    category: 'Категория',
    subcategories: 'Подкатегории',
    branchId: 'Центр выдачи',
    depositPerUnit: 'Депозит',
    brand: 'Бренд',
    model: 'Модель',
    condition: 'Состояние',
    year: 'Год выпуска',
    color: 'Цвет',
    size: 'Размер',
    weight: 'Вес',
    material: 'Материал',
    season: 'Сезон',
    insoleLengthCm: 'Размер в см',
    // Добавьте другие поля по необходимости
  };

  // Поля, которые нужно исключить из specificFields (они отображаются отдельно)
  const excludedFields = ['branchId', 'depositPerUnit'];

  // Функция для получения читаемого названия поля
  const getFieldLabel = (key) => {
    return fieldLabels[key] || key;
  };

  // Формируем список параметров для отображения
  const getParameters = () => {
    const params = [];

    // Категория
    if (item.category) {
      params.push({ label: 'Категория', value: item.category });
    }

    // Подкатегории
    if (item.subcategories && item.subcategories.length > 0) {
      params.push({ label: 'Подкатегории', value: item.subcategories.join(', ') });
    }

    // Центр выдачи - показываем только если есть адрес
    if (branchAddress) {
      params.push({ label: 'Центр выдачи', value: branchAddress });
    }

    // Депозит - приоритетно берем из specificFields.depositPerUnit, если нет - из item.depositPerUnit
    let depositValue = null;
    if (item.specificFields && item.specificFields.depositPerUnit !== undefined && item.specificFields.depositPerUnit !== null) {
      depositValue = Number(item.specificFields.depositPerUnit);
    } else if (item.depositPerUnit !== undefined && item.depositPerUnit !== null) {
      depositValue = Number(item.depositPerUnit);
    }
    
    if (depositValue !== null && !isNaN(depositValue) && depositValue > 0) {
      params.push({ label: 'Депозит', value: `${depositValue} ₽` });
    }

    // Дополнительные поля из specificFields (исключаем branchId и depositPerUnit, они отображаются отдельно)
    if (item.specificFields && typeof item.specificFields === 'object' && !Array.isArray(item.specificFields)) {
      Object.entries(item.specificFields).forEach(([key, value]) => {
        // Пропускаем поля, которые уже отображаются отдельно
        if (excludedFields.includes(key)) {
          return;
        }
        if (value && value.toString().trim() !== '') {
          const label = getFieldLabel(key);
          params.push({ label, value: value.toString() });
        }
      });
    }

    return params;
  };

  const parameters = getParameters();

  return (
    <Container sx={{ paddingTop: 4, paddingBottom: 4, backgroundColor: '#F5F3F0', minHeight: '100vh' }}>
      <Box className="item-detail-layout">
        <Box className="item-detail-gallery">
          <Gallery images={images} />
        </Box>

        <Box className="item-detail-content">
          <Box className="item-description" sx={{ mt: { xs: 3, md: 0 }, mb: 3 }}>
            <CardTitleRow
              type="item"
              entityId={item._id}
              titleVariant="h4"
              titleClassName="item-title"
              gutterBottom
              titleSx={{ color: '#000000', fontWeight: 'bold' }}
            >
              {item.title || item.name}
            </CardTitleRow>
            <Typography variant="h5" sx={{ mt: 2, mb: 2, color: '#8B7D6B', fontWeight: 'bold' }}>
              Цена: {item.price} ₽/сутки
            </Typography>
            <Typography variant="body1" sx={{ color: '#000000', lineHeight: 1.8, mb: 3 }}>
              {item.description}
            </Typography>
          </Box>

          {parameters.length > 0 && (
            <Box className="item-parameters" sx={{ mt: 3, mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#000000', fontWeight: 'bold' }}>
                Параметры
              </Typography>
              <List sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: 2,
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                padding: 0,
              }}>
                {parameters.map((param, index) => (
                  <React.Fragment key={index}>
                    <ListItem sx={{ py: 1.5 }}>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#8B7D6B' }}>
                            {param.label}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body1" sx={{ color: '#000000', mt: 0.5 }}>
                            {param.value}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < parameters.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}
        </Box>
      </Box>

      {/* Кнопка возврата наверх */}
      {showScrollToTop && (
        <Button
          onClick={scrollToTop}
          className="scroll-to-top-button"
          sx={{
            position: 'fixed',
            bottom: '80px',
            right: '16px',
            width: '50px',
            height: '50px',
            minWidth: '0',
            minHeight: '0',
            borderRadius: '50%',
            padding: '0',
            backgroundColor: '#8B7D6B',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
            '&:hover': {
              backgroundColor: '#6B5D4B',
            },
          }}
        >
          <ArrowUpIcon />
        </Button>
      )}

      {/* Кнопка добавления в корзину */}
      <Button
        onClick={handleAddToCart}
        variant="contained"
        disabled={loading}
        fullWidth={isMobile}
        sx={{
          position: isMobile ? 'relative' : 'fixed',
          bottom: isMobile ? 'auto' : '20px',
          right: isMobile ? 'auto' : '16px',
          mt: isMobile ? 2 : 0,
          mb: isMobile ? 10 : 0, // Отступ снизу для мобильных, чтобы не перекрывалось нижним меню
          backgroundColor: '#8B7D6B',
          color: 'white',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
          '&:hover': {
            backgroundColor: '#6B5D4B',
          },
          '&:disabled': {
            backgroundColor: '#8B7D6B',
            opacity: 0.6,
          },
        }}
      >
        {loading ? 'Добавление...' : success ? 'Товар добавлен!' : 'Добавить в корзину'}
      </Button>

    </Container>
  );
};

export default ItemPage;
